/**
 * Dashboard wrapper — bridges API data to the existing PEDashboard component.
 *
 * Fetches data from the API via usePortfolioData(), passes it as props
 * to the existing dashboard component. Handles loading/error/empty states.
 */
import { Link } from 'react-router-dom'
import { useAuth } from './Auth/AuthContext'
import { usePortfolioData } from '../hooks/usePortfolioData'
import { useSimulationSettings } from '../hooks/useSimulationSettings'
import PEDashboard from './PEDashboard'

const btnPrimary =
  'px-4 py-2 bg-brand hover:opacity-90 text-ink-onAccent rounded-lg text-sm transition'

export default function Dashboard() {
  const { currentOrg, orgError, refreshOrgs } = useAuth()
  const orgId = currentOrg?.id

  const { data, fundVariables, fxData, loading, error, refresh } = usePortfolioData(orgId)
  const { settings: simSettings, updateSettings } = useSimulationSettings(orgId)

  if (orgError) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-60px)]">
        <div className="text-center max-w-md">
          <div className="text-warning-fg text-xl mb-2">Could not load organization</div>
          <div className="text-ink-subtle text-sm mb-4">{orgError}</div>
          <button
            onClick={refreshOrgs}
            className={btnPrimary}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-60px)]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-ink-muted">Loading portfolio data...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-60px)]">
        <div className="text-center max-w-md">
          <div className="text-danger-fg text-xl mb-2">Failed to load data</div>
          <div className="text-ink-subtle text-sm mb-4">{error}</div>
          <button
            onClick={refresh}
            className={btnPrimary}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!data || !data.funds || data.funds.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-60px)]">
        <div className="text-center max-w-md">
          <div className="text-ink-muted text-xl mb-2">No portfolio data yet</div>
          <div className="text-ink-subtle text-sm mb-4">
            Upload your CAS statements and drawdown documents to get started.
          </div>
          <Link
            to="/import"
            className={`inline-block px-6 py-2 ${btnPrimary}`}
          >
            Import Data
          </Link>
        </div>
      </div>
    )
  }

  return (
    <PEDashboard
      rawData={data}
      fundVariablesData={fundVariables}
      fxData={fxData}
      simulationSettings={simSettings}
      onSimulationChange={updateSettings}
      orgId={orgId}
    />
  )
}
